import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Trans, useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import type {
  DigestSettings,
  EventChannelPreference,
  NotificationEventDescriptor,
  NotificationEventGroup,
  NotificationEventType,
  NotificationPreferences,
} from '@/lib/types/preferences'
import { usePageLayout } from '@/hooks/usePageLayout'
import { useWebPushRegistration } from '@/hooks/useWebPushRegistration'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import SettingsSidebar from '@/components/app/sidebars/settings'
import {
  SavedTick,
  SettingsCard,
  SettingsPage,
  SettingsPageLoading,
  SettingsRow,
  SettingsSection,
  settingsInputClass,
} from '@/components/app/settings'
import {
  NOTIFICATION_EVENT_TYPES,
  NOTIFICATION_NOTICE_KEYS,
  defaultEventChannelPreference,
  parseNotificationPreferences,
} from '@/lib/types/preferences'
import {
  useUpdateUserPreferences,
  useUserPreferences,
} from '@/lib/db/rxdb/hooks/entities/user-preferences'
import useLoginStore from '@/store/LoginStore'
import { getReminderMigrationStatus } from '@/lib/api/features/reminders/api'
import { describeBrowser } from '@/lib/utils/describeBrowser'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { cn } from '@/lib/utils'

type ChannelKey = keyof EventChannelPreference

/** Column order of the per-event table. Every channel here exists in the model. */
const CHANNELS: Array<ChannelKey> = ['app', 'email', 'push', 'digest']

/** Where the last write came from, so the right "Saved" tick lights up. */
type SaveScope = NotificationEventGroup | 'digest' | 'push' | null

export default function NotificationSettingsPage() {
  const { t } = useTranslation('settings')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const { data: userPrefs, isLoading } = useUserPreferences()
  const updatePrefs = useUpdateUserPreferences()
  const { isPersonal } = useWorkspaceMode()
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const webPush = useWebPushRegistration()

  const [isSaving, setIsSaving] = useState(false)
  // Guards against two quick toggles both writing from the same stale snapshot.
  const savingRef = useRef(false)
  const [savedScope, setSavedScope] = useState<SaveScope>(null)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current)
    },
    [],
  )
  const [showReminderMigrationBanner, setShowReminderMigrationBanner] = useState(false)

  // Display-only normalization. Never auto-write the canonical shape back.
  const prefs: NotificationPreferences = useMemo(
    () => parseNotificationPreferences(userPrefs?.notificationsJson),
    [userPrefs?.notificationsJson],
  )

  const userTimezone =
    userPrefs?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone

  // Reminder migration banner — endpoint reports whether the user has any
  // active non-app-channel reminders. Show only if not yet dismissed.
  useEffect(() => {
    if (!workspaceId) return
    const noticeKey = NOTIFICATION_NOTICE_KEYS.ReminderChannelMigration
    const dismissed = Boolean(prefs.dismissedNotices?.[noticeKey])
    if (dismissed) {
      setShowReminderMigrationBanner(false)
      return
    }

    let cancelled = false
    getReminderMigrationStatus(workspaceId)
      .then((status) => {
        if (!cancelled) setShowReminderMigrationBanner(status.hasNonAppReminders)
      })
      .catch((error) => {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'NotificationSettingsPage',
          action: 'reminder_migration_status',
        })
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, prefs.dismissedNotices])

  const savePreferences = useCallback(
    async (updated: NotificationPreferences, scope: SaveScope) => {
      savingRef.current = true
      setIsSaving(true)
      try {
        await updatePrefs({ notificationsJson: JSON.stringify(updated) })
        // "✓ Saved" next to the section that changed, for a couple of seconds.
        if (savedTimer.current) clearTimeout(savedTimer.current)
        setSavedScope(scope)
        savedTimer.current = setTimeout(() => setSavedScope(null), 2000)
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'NotificationSettingsPage',
          action: 'save_preferences',
        })
        toast.error(t('preferencesPage.toast.updateFailed'))
      } finally {
        savingRef.current = false
        setIsSaving(false)
      }
    },
    [updatePrefs, t],
  )

  const getEventPref = (eventType: NotificationEventType): EventChannelPreference => {
    return prefs.eventPreferences?.[eventType] ?? defaultEventChannelPreference(eventType)
  }

  const getDigest = (): DigestSettings => {
    return prefs.digest ?? { enabled: false, time: '09:00', frequency: 'weekdays' }
  }

  /**
   * Toggle a per-event channel.
   *
   * Special cases:
   * - Push: turning ON while this browser isn't subscribed triggers browser
   *   registration (permission prompt + PushSubscription + backend register).
   *   If the user denies permission OR the backend has no VAPID keys, we
   *   surface a toast and do NOT persist the toggle.
   * - Digest: turning a Digest cell on while the master digest is off also
   *   flips the master on in the SAME save, so one click does what it says.
   */
  const updateEventPref = async (
    eventType: NotificationEventType,
    group: NotificationEventGroup,
    channel: ChannelKey,
    value: boolean,
  ) => {
    if (savingRef.current) return

    if (channel === 'push' && value && !webPush.isSubscribed) {
      if (webPush.status === 'unsupported') {
        toast.error(t('notificationsPage.push.unsupported'))
        return
      }
      if (webPush.status === 'not-configured') {
        toast.error(t('notificationsPage.push.notConfigured'))
        return
      }
      if (webPush.status === 'permission-denied') {
        toast.error(t('notificationsPage.push.permissionDenied'))
        return
      }
      const ok = await webPush.enable()
      if (!ok) {
        toast.error(t('notificationsPage.push.enableFailed'))
        return
      }
    }

    const currentEventPrefs: Record<string, EventChannelPreference> =
      prefs.eventPreferences ?? {}
    const currentPref = currentEventPrefs[eventType] ?? defaultEventChannelPreference(eventType)
    const currentDigest = getDigest()

    const updated: NotificationPreferences = {
      ...prefs,
      eventPreferences: {
        ...currentEventPrefs,
        [eventType]: {
          ...currentPref,
          [channel]: value,
        },
      },
    }

    if (channel === 'digest' && value && !currentDigest.enabled) {
      updated.digest = { ...currentDigest, enabled: true }
      toast.success(t('notificationsPage.digestSection.autoEnableToast'))
    }

    void savePreferences(updated, group)
  }

  const updateDigest = (updates: Partial<DigestSettings>) => {
    if (savingRef.current) return
    void savePreferences({ ...prefs, digest: { ...getDigest(), ...updates } }, 'digest')
  }

  const dismissReminderMigrationBanner = () => {
    const noticeKey = NOTIFICATION_NOTICE_KEYS.ReminderChannelMigration
    setShowReminderMigrationBanner(false)
    void savePreferences(
      {
        ...prefs,
        dismissedNotices: {
          ...(prefs.dismissedNotices ?? {}),
          [noticeKey]: new Date().toISOString(),
        },
      },
      null,
    )
  }

  // Format timezone offset for display
  const timezoneOffset = (() => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        timeZoneName: 'shortOffset',
      })
      const tz = formatter.formatToParts(new Date()).find((p) => p.type === 'timeZoneName')
      return tz?.value ?? ''
    } catch {
      return ''
    }
  })()

  if (isLoading || !userPrefs) {
    return <SettingsPageLoading />
  }

  const digest = getDigest()
  const pushUnavailable =
    webPush.status === 'unsupported' ||
    webPush.status === 'not-configured' ||
    webPush.status === 'permission-denied'

  // Filter rows: in Personal workspaces, hide events that require multiple
  // actors (assigned/mentioned/commented/status_changed/page.*).
  const visibleEvents = NOTIFICATION_EVENT_TYPES.filter(
    (e) => !isPersonal || e.applicableInPersonal,
  )

  // Group by event.group, preserving the order in NOTIFICATION_EVENT_TYPES.
  const groups: Array<{ key: NotificationEventGroup; events: Array<NotificationEventDescriptor> }> = []
  for (const event of visibleEvents) {
    let group = groups.find((g) => g.key === event.group)
    if (!group) {
      group = { key: event.group, events: [] }
      groups.push(group)
    }
    group.events.push(event)
  }

  const channelLabel: Record<ChannelKey, string> = {
    app: t('notificationsPage.matrix.columnApp'),
    email: t('notificationsPage.matrix.columnEmail'),
    push: t('notificationsPage.matrix.columnPush'),
    digest: t('notificationsPage.matrix.columnDigest'),
  }

  // Why a column looks muted: surfaced once under the table it affects.
  const channelMuted: Record<ChannelKey, boolean> = {
    app: !prefs.enableAppNotifications,
    email: false,
    push: webPush.status !== 'subscribed' && webPush.status !== 'idle',
    digest: !digest.enabled,
  }

  const hints = [
    !prefs.enableAppNotifications && t('notificationsPage.matrix.appOffHint'),
    !digest.enabled && t('notificationsPage.matrix.digestOffHint'),
  ].filter(Boolean) as Array<string>

  return (
    <SettingsPage
      title={t('notificationsPage.title')}
      description={t('notificationsPage.description')}
    >
      {/* Reminder migration banner */}
      {showReminderMigrationBanner && (
        <div className="flex items-start justify-between gap-3 rounded-[14px] border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
          <p className="text-sm text-amber-900 dark:text-amber-100">
            {t('notificationsPage.banners.reminderChannelMigration')}
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={dismissReminderMigrationBanner}
            disabled={isSaving}
            aria-label={t('notificationsPage.banners.dismiss')}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}

      <PushStatusLine
        status={webPush.status}
        onEnable={async () => {
          const ok = await webPush.enable()
          if (!ok) toast.error(t('notificationsPage.push.enableFailed'))
        }}
        onDisable={() => void webPush.disable()}
      />

      {groups.map((group, index) => (
        <SettingsSection
          key={group.key}
          title={t(`notificationsPage.groups.${group.key}`)}
          aside={
<SavedTick visible={savedScope === group.key} />
          }
        >
          <SettingsCard>
            {/* Fixed layout + colgroup keeps the switch columns aligned across
                tables, even where the header row is visually hidden.
                Column widths follow the table, not the viewport: a docked
                sidebar can leave a ~320px table on a 700px window, and the
                sm: sizes then give the label column 0px (MOL-40). */}
            <div className="@container">
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">{t(`notificationsPage.groups.${group.key}`)}</caption>
              <colgroup>
                <col />
                {CHANNELS.map((channel, i) => (
                  <col
                    key={channel}
                    className={
                      i === CHANNELS.length - 1 ? 'w-[68px] @min-[520px]:w-[92px]' : 'w-14 @min-[520px]:w-[76px]'
                    }
                  />
                ))}
              </colgroup>
              <thead className={cn(index > 0 && 'sr-only')}>
                <tr className="border-b bg-secondary/40">
                  <th
                    scope="col"
                    className="px-[18px] py-2.5 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase"
                  >
                    {t('notificationsPage.matrix.header')}
                  </th>
                  {CHANNELS.map((channel) => (
                    <th
                      key={channel}
                      scope="col"
                      className={cn(
                        'px-1 py-2.5 text-center text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase',
                        channel === CHANNELS[CHANNELS.length - 1] && 'pr-3 sm:pr-4',
                      )}
                    >
                      {channelLabel[channel]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {group.events.map((event) => {
                  const eventPref = getEventPref(event.key)
                  const eventLabel = t(event.labelKey)
                  return (
                    <tr key={event.key}>
                      <th scope="row" className="px-[18px] py-3 font-normal">
                        <span className="block text-sm font-medium break-words text-foreground">
                          {eventLabel}
                        </span>
                      </th>
                      {CHANNELS.map((channel) => (
                        <td
                          key={channel}
                          className={cn(
                            'px-1 py-3 text-center',
                            channel === CHANNELS[CHANNELS.length - 1] && 'pr-3 sm:pr-4',
                          )}
                        >
                          <Switch
                            checked={eventPref[channel]}
                            onCheckedChange={(checked) =>
                              void updateEventPref(event.key, group.key, channel, checked)
                            }
                            disabled={channel === 'push' && pushUnavailable}
                            aria-label={t('notificationsPage.matrix.cellLabel', {
                              event: eventLabel,
                              channel: channelLabel[channel],
                            })}
                            className={cn(channelMuted[channel] && 'opacity-50')}
                          />
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
          </SettingsCard>
          {index === 0 && hints.length > 0 && (
            <div className="space-y-0.5 px-0.5">
              {hints.map((hint) => (
                <p key={hint} className="text-xs break-words text-muted-foreground">
                  {hint}
                </p>
              ))}
            </div>
          )}
        </SettingsSection>
      ))}

      {/* Email digest: master switch + schedule */}
      <SettingsSection
        title={t('notificationsPage.digestSection.title')}
        aside={
<SavedTick visible={savedScope === 'digest'} />
        }
      >
        <SettingsCard>
          <SettingsRow
            label={t('notificationsPage.digestSection.enableLabel')}
            description={t('notificationsPage.digestSection.enableHint')}
            htmlFor="digest-enabled"
          >
            <Switch
              id="digest-enabled"
              checked={digest.enabled}
              onCheckedChange={(checked) => updateDigest({ enabled: checked })}
            />
          </SettingsRow>
          {digest.enabled && (
            <SettingsRow
              label={t('notificationsPage.digestSection.scheduleLabel')}
              description={t('notificationsPage.digestSection.timezoneSuffix', {
                offset: timezoneOffset,
              })}
            >
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <Select
                  value={digest.frequency}
                  onValueChange={(value) =>
                    updateDigest({ frequency: value as DigestSettings['frequency'] })
                  }
                >
                  <SelectTrigger
                    className="h-9 flex-1 rounded-lg bg-card sm:w-[140px] sm:flex-none"
                    aria-label={t('notificationsPage.digestSection.frequencyLabel')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekdays">
                      {t('notificationsPage.digestSection.frequencyWeekdays')}
                    </SelectItem>
                    <SelectItem value="daily">
                      {t('notificationsPage.digestSection.frequencyDaily')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <input
                  type="time"
                  value={digest.time}
                  onChange={(e) => {
                    if (e.target.value) updateDigest({ time: e.target.value })
                  }}
                  aria-label={t('notificationsPage.digestSection.timeLabel')}
                  className={cn(settingsInputClass, 'flex-1 sm:w-[140px] sm:flex-none')}
                />
              </div>
            </SettingsRow>
          )}
        </SettingsCard>
      </SettingsSection>
    </SettingsPage>
  )
}

type PushStatus = ReturnType<typeof useWebPushRegistration>['status']

/** One line at the top: is push on for this browser, and the one action that changes it. */
function PushStatusLine({
  status,
  onEnable,
  onDisable,
}: {
  status: PushStatus
  onEnable: () => void | Promise<void>
  onDisable: () => void
}) {
  const { t } = useTranslation('settings')
  const [busy, setBusy] = useState(false)
  const { browser, os } = describeBrowser(
    typeof navigator === 'undefined' ? '' : navigator.userAgent,
  )
  const device =
    browser && os
      ? t('notificationsPage.push.device', { browser, os })
      : (browser ?? os ?? '')

  const run = async (fn: () => void | Promise<void>) => {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  let dot = 'bg-muted-foreground/50'
  let body: React.ReactNode
  let action: React.ReactNode = null

  if (status === 'subscribed') {
    dot = 'bg-emerald-500'
    body = (
      <>
        <Trans t={t} i18nKey="notificationsPage.push.onFor" components={{ b: <strong className="font-semibold" /> }} />
        {device && <span className="text-muted-foreground"> ({device})</span>}
      </>
    )
    action = (
      <button
        type="button"
        onClick={() => void run(onDisable)}
        disabled={busy}
        className="shrink-0 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
      >
        {t('notificationsPage.push.turnOff')}
      </button>
    )
  } else if (status === 'idle') {
    body = (
      <>
        <Trans t={t} i18nKey="notificationsPage.push.offFor" components={{ b: <strong className="font-semibold" /> }} />
        {device && <span className="text-muted-foreground"> ({device})</span>}
      </>
    )
    action = (
      <button
        type="button"
        onClick={() => void run(onEnable)}
        disabled={busy}
        className="shrink-0 font-medium text-foreground underline-offset-4 hover:underline disabled:opacity-50"
      >
        {t('notificationsPage.push.turnOn')}
      </button>
    )
  } else if (status === 'permission-denied') {
    dot = 'bg-amber-500'
    body = t('notificationsPage.push.permissionDenied')
  } else if (status === 'not-configured') {
    body = t('notificationsPage.push.notConfigured')
  } else {
    body = t('notificationsPage.push.unsupported')
  }

  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-[14px] border bg-card px-[18px] py-3 text-sm"
    >
      <span aria-hidden className={cn('size-2 shrink-0 rounded-full', dot)} />
      <p className="min-w-0 flex-1 text-foreground">{body}</p>
      {action}
    </div>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/notifications',
)({
  component: NotificationSettingsPage,
})
